<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { debounce } from "lodash-es";
import { useI18n } from "vue-i18n";

import { toast } from "@/common/utils/toast";

import { analysisPatch } from "@/common/api/rest/analysis/analysis";
import { evaluationEmployeeCreate } from "@/common/api/rest/evaluation/employee";
import {
  employeeCreate,
  employeeShow,
  employeePatch,
} from "@/common/api/rest/employee";

import type { AnalysisType } from "@/common/types/analysis";
import type { CompanyType } from "@/common/types/company";
import type {
  EmployeePayloadType,
  EmployeeListType,
} from "@/common/types/employee";

import { employeeForm, searchEmployee } from "@/components";
import {
  svgIcon,
  baseModal,
  baseConfirm,
  baseTooltip,
} from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: AnalysisType | null;
    company: CompanyType | null;
    width?: string;
    isEdit?: boolean;
  }>(),
  {
    width: "100%",
    isEdit: true,
  },
);

const employeeData = ref<EmployeePayloadType | null>(null);
const selected = ref<EmployeeListType | null>(null);
const inputSearch = ref("");

const showModalAdd = ref(false);
const showModalNew = ref(false);
const showDelConfirm = ref(false);

function fetchData() {
  if (!props.data?.employee_id) return;

  employeeShow(props.data.account_id, props.data.employee_id)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        employeeData.value = {
          ...data,
          name: data.name,
        };
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function update() {
  emit("update-data");
}

function updateEmployee(payload: EmployeePayloadType) {
  if (!props.data?.employee_id) return;

  employeePatch(props.data.account_id, props.data.employee_id, payload)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        toast({ message: message, type: "success" });
        update();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function patchData(payload: Partial<AnalysisType>) {
  if (!props.data) return;

  analysisPatch(props.data.account_id, props.data.analysis_id, payload)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        toast({ message: message, type: "success" });
        update();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function add() {
  showModalAdd.value = true;
}

function addSelect(data: any[]) {
  selected.value = data[0];
}

function addCancel() {
  selected.value = null;
  showModalAdd.value = false;
}

function addConfirm() {
  if (!props.data) return;

  patchData({
    employee_id: selected.value?.employee_id,
    employee_name: selected.value?.name,
    employee_registration_number: selected.value?.registration_number,
  });

  inputSearch.value = "";
  addCancel();
}

function del() {
  showDelConfirm.value = true;
}

function delCancel() {
  showDelConfirm.value = false;
}

function delConfirm() {
  patchData({ employee_id: null });
  delCancel();
}

function newRegister(name: string) {
  showModalNew.value = true;
  showModalAdd.value = false;
  selected.value = null;
  employeeData.value = { name } as never;
}

function newRegisterCancel() {
  showModalNew.value = false;
  selected.value = null;
  employeeData.value = null;
}

function evaluationAddEmployee(employee_id: string) {
  if (!props.data) return;

  evaluationEmployeeCreate(props.data.account_id, {
    evaluation_id: props.data.evaluation_id,
    employee_id,
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        patchData({ employee_id });
        newRegisterCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function newRegisterConfirm() {
  if (!props.company?.company_id) {
    toast({ message: "Primeiro adicione uma empresa", type: "error" });
    return;
  }

  showModalNew.value = false;

  employeeCreate(
    props.data.account_id,
    props.company.company_id,
    employeeData.value as EmployeePayloadType,
  )
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        evaluationAddEmployee(data.employee_id);
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

const debounceForm = debounce(updateEmployee, 1000);

watch(
  () => props.data?.employee_id,
  () => {
    fetchData();
  },
);

onMounted(() => {
  fetchData();
});
</script>

<template>
  <section class="tab-component" :class="{ width: width }">
    <div class="content">
      <template v-if="!data?.employee_id">
        <div class="form-empty">
          {{ t("analysis.texts.empty_employee") }}
          <button class="btn btn-md btn-primary" @click="add()">
            {{ t("common.labels.add").toUpperCase() }}
          </button>
        </div>
      </template>

      <template v-else>
        <div class="action">
          <base-tooltip position="relative">
            <template #trigger>
              <button
                type="button"
                class="btn btn-none btn-sm remove"
                @click="del()"
              >
                <svg-icon icon="trash" size="1.75rem" />
              </button>
            </template>
            <template #tooltip>
              {{ t("common.labels.delete") }}
            </template>
          </base-tooltip>
        </div>
        <employee-form
          :data="employeeData"
          :is-patch="true"
          @update-data="debounceForm"
        />
      </template>
    </div>

    <base-modal
      :show="showModalAdd"
      :title="
        t('common.labels.add', {
          concat: t('common.labels.employee'),
        }).toUpperCase()
      "
      :show-button-confirm="true"
      :button-confirm="t('common.labels.add')"
      :button-close="t('common.labels.cancel')"
      @close="addCancel()"
      @confirm="addConfirm()"
    >
      <template #content>
        <search-employee
          :account-id="data?.account_id"
          :company-id="company?.company_id"
          :ids="data?.employee_id ? [data.employee_id] : []"
          :add="true"
          :unic="true"
          :is-patch="false"
          @update-data="addSelect"
          @new:register="newRegister"
        />
      </template>
    </base-modal>

    <base-modal
      :show="showModalNew"
      :title="
        t('common.labels.add', {
          concat: t('common.labels.employee'),
        }).toUpperCase()
      "
      :show-button-confirm="true"
      :button-confirm="t('common.labels.add')"
      :button-close="t('common.labels.cancel')"
      @close="newRegisterCancel()"
      @confirm="newRegisterConfirm()"
    >
      <template #content>
        <employee-form
          :data="employeeData"
          :is-patch="false"
          @update-data="employeeData = $event"
        />
      </template>
    </base-modal>

    <base-confirm
      :show="showDelConfirm"
      max-width="400px"
      :title="
        t('common.labels.remove', {
          concat: t('common.labels.employee'),
        }).toUpperCase()
      "
      :message="
        t('common.texts.remove_message', {
          concat: employeeData?.name,
        })
      "
      @cancel="delCancel"
      @confirm="delConfirm"
    />
  </section>
</template>

<style lang="scss" scoped>
.tab-component {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding-block: 1rem;
  padding-inline: 1.5rem;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.15rem;
    font-weight: bold;
    text-transform: uppercase;
    border-bottom: 1px solid var(--border);
    padding-block-end: 0.25rem;
  }

  .search {
    width: 100%;

    .icon {
      cursor: pointer;
      color: var(--text-off);

      &:hover {
        color: var(--primary);
      }
    }
  }

  .list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-block: 1rem;
    padding-inline: 0.5rem;

    .item {
      cursor: pointer;
      user-select: none;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.7rem;
      padding-block: 0.75rem;
      border-block-end: 1px solid var(--border);

      .company {
        flex: 3;

        .name {
          font-weight: bold;
        }

        .document {
          font-size: 0.875rem;
        }
      }
    }
  }

  .border {
    border-block-end: 1px solid var(--border);
    padding-block-end: 0.4rem;
  }

  .company-data {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin-block: 1rem;

    .header {
      display: flex;
      flex-direction: row;
      gap: 1rem;
      margin-block-end: 1rem;

      .company {
        flex: 2;

        .name {
          font-size: 1.15rem;
          font-weight: bold;
        }

        .document {
          font-size: 0.875rem;
        }
      }

      .action {
        .remove {
          &:hover {
            color: var(--red);
          }
        }
      }
    }

    .addresses {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;

      .item {
        display: flex;
        flex-direction: row;
        gap: 1rem;
        padding-inline: 1rem;

        .infos {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
      }
    }

    .descriptions {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;

      .label {
        font-weight: bold;
      }

      .value {
        font-size: 0.9rem;
        text-align: justify;
        padding-inline: 1rem;
      }
    }

    .contacts {
      display: flex;
      flex-direction: column;
      gap: 1rem;

      .item {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        padding-inline: 1rem;
        padding-block-end: 0.5rem;

        .name {
          font-weight: bold;
        }

        .values {
          span {
            font-weight: bold;
          }
        }
      }
    }
  }

  .form-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.5rem;
    padding-block: 1.5rem;
  }

  .action {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    padding-block-end: 1rem;

    .remove {
      &:hover {
        color: var(--red);
      }
    }
  }
}
</style>
